import React from 'react';
import { Link } from 'react-router-dom';


/* ─── Brand tokens ──────────────────────────────────────────────────────────── */
const C = {
  paper:      '#f7f3ea',
  paper2:     '#fbf9f3',
  navy:       '#16304a',
  deepGreen:  '#0e2d22',   /* TWA product dark — forest green (not OP navy) */
  deepGreen2: '#163529',   /* lighter TWA dark */
  teal:       '#008C86',   /* TWA accent = OP teal */
  tealSoft:   '#ddf5f1',
  gold:       '#c79a3a',   /* used sparingly on CTAs only */
  border:     '#e2d8c4',
  border2:    '#ece5d6',
  muted:      '#7d9bb0',
  mutedTxt:   '#6c6258',
  ink:        '#1a1a1a',
};

const SERIF = "'Newsreader', Georgia, serif";
const SANS  = "'Public Sans', system-ui, sans-serif";

/* ─── Static data ───────────────────────────────────────────────────────────── */
const STATS = [
  { val: '~8 MIN',  lbl: 'AVERAGE READ' },
  { val: '5–8',     lbl: 'STORIES PER EDITION' },
  { val: 'FRIDAY',  lbl: 'EVERY WEEK' },
  { val: '4',       lbl: 'COVERAGE AREAS' },
  { val: '4 WEEKS', lbl: 'ROLLING ACCESS\nFOR MEMBERS' },
];

const EDITIONS = [
  { vol: 'VOL. 1 NO. 23', date: 'MAY 23, 2026', title: 'Markets, Policy & The Jobs Data That Surprised Everyone',         stories: 7, readTime: '9 MIN' },
  { vol: 'VOL. 1 NO. 22', date: 'MAY 16, 2026', title: 'AI Investment Surge — What Leaders Need to Know',                stories: 6, readTime: '8 MIN' },
  { vol: 'VOL. 1 NO. 21', date: 'MAY 9, 2026',  title: "Federal Budget Watch: Winners, Losers, and What's Next",          stories: 8, readTime: '10 MIN' },
  { vol: 'VOL. 1 NO. 20', date: 'MAY 2, 2026',  title: 'The April Jobs Report and the Sectors Moving First',              stories: 7, readTime: '9 MIN' },
  { vol: 'VOL. 1 NO. 19', date: 'APR 25, 2026', title: 'Healthcare Policy Shifts and the Career Impact',                 stories: 6, readTime: '8 MIN' },
];

const COVERAGE = [
  { num: '01', label: 'Macro Outlook',         desc: 'Economy, inflation, rate signals, and the global indicators shaping the week ahead.' },
  { num: '02', label: 'Policy Watch',          desc: 'Federal and state legislation, regulatory updates, and the opportunities they unlock.' },
  { num: '03', label: 'Opportunity Scan',      desc: 'Emerging sectors, growth signals, and where real investment and talent are moving.' },
  { num: '04', label: 'Moves & Appointments',  desc: 'Key hires, promotions, and leadership transitions — and why they matter right now.' },
];

const TESTIMONIALS = [
  { name: 'Marcus R.',  role: 'Chief Investment Officer',  quote: "The Week Ahead is the only briefing I read cover-to-cover. The depth-to-length ratio is unmatched." },
  { name: 'Denise W.',  role: 'VP of Workforce Strategy',  quote: "Every Friday I clear 10 minutes for this. The Opportunity Scan alone has changed how we think about pipeline." },
  { name: 'Alan T.',    role: 'Senior Policy Analyst',     quote: "Policy Watch cuts through the noise faster than anything else I've found. It's become standard reading for our whole team." },
];

/* ─── Horizon lines (decorative teal lines — forward-looking motion) ─────── */
const HorizonLines = () => (
  <svg
    viewBox="0 0 260 24"
    width="260"
    height="24"
    aria-hidden="true"
    style={{ display: 'block', marginTop: 6, marginBottom: 6 }}
  >
    {[0, 8, 16].map((y, i) => (
      <line
        key={y}
        x1={i * 16}
        y1={y + 2}
        x2={260 - i * 22}
        y2={y + 2}
        stroke={C.teal}
        strokeWidth="2.5"
        strokeLinecap="round"
        opacity={1 - i * 0.3}
      />
    ))}
  </svg>
);

/* ─── Read-lines icon (edition indicator) ───────────────────────────────────── */
const ReadIcon = ({ active = false }) => (
  <div style={{
    width: 32, height: 32, borderRadius: '50%',
    border: `1.5px solid ${active ? C.teal : C.border2}`,
    background: active ? C.teal : '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  }}>
    <svg viewBox="0 0 16 16" width="10" height="10" fill="none"
      stroke={active ? '#fff' : C.navy} strokeWidth="1.5" strokeLinecap="round">
      <path d="M3 5h10M3 8h7M3 11h5" />
    </svg>
  </div>
);

/* ─── Trust icon row ─────────────────────────────────────────────────────────── */
const TrustRow = ({ label, light = false }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none">
      <circle cx="10" cy="10" r="9" stroke={C.teal} strokeWidth="1.5" />
      <polyline points="6,10 9,13 14,7" stroke={C.teal} strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <span style={{
      fontSize: 12.5,
      color: light ? 'rgba(255,255,255,0.72)' : C.mutedTxt,
      fontFamily: SANS,
    }}>
      {label}
    </span>
  </div>
);

/* ─── Page component ─────────────────────────────────────────────────────────── */
export default function TheWeekAheadPage() {
  return (
    <div style={{ background: C.paper, fontFamily: SANS, color: C.navy }}
      data-testid="week-ahead-page">

      {/* ══════════════════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════════════════ */}
      <section style={{ background: C.paper, borderBottom: `1px solid ${C.border}` }}>
        <div className="container-custom" style={{
          paddingTop: 'clamp(44px, 6vw, 80px)',
          paddingBottom: 'clamp(44px, 6vw, 80px)',
        }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 1fr)',
              gap: 'clamp(32px, 5vw, 64px)',
              alignItems: 'center',
            }}
            className="twa-hero-grid"
          >

            {/* ── Left: Identity lockup ── */}
            <div>
              {/* Eyebrow */}
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: C.teal, marginBottom: 18,
                fontFamily: SANS,
              }}>
                THE FRIDAY BRIEFING · FROM OPPORTUNITY POST.
              </div>

              {/* "THE WEEK" line */}
              <div style={{
                fontSize: 'clamp(58px, 9vw, 104px)',
                fontWeight: 900,
                letterSpacing: '-0.04em',
                lineHeight: 0.92,
                color: C.navy,
                fontFamily: SANS,
              }}>
                THE WEEK
              </div>

              {/* Horizon decoration */}
              <HorizonLines />

              {/* "AHEAD" in teal — the forward-looking accent */}
              <div style={{
                fontSize: 'clamp(58px, 9vw, 104px)',
                fontWeight: 900,
                letterSpacing: '-0.04em',
                lineHeight: 0.92,
                color: C.teal,
                fontFamily: SANS,
                marginTop: -2,
              }}>
                AHEAD
              </div>

              {/* Tagline */}
              <div style={{
                marginTop: 24,
                fontSize: 'clamp(12px, 1.4vw, 14.5px)',
                fontWeight: 800,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: C.navy,
                fontFamily: SANS,
              }}>
                CLARITY FOR THE WEEK AHEAD.
              </div>
            </div>

            {/* ── Right: Pitch panel ── */}
            <div style={{
              borderLeft: `1.5px solid ${C.border}`,
              paddingLeft: 'clamp(24px, 4vw, 48px)',
            }}>
              <div style={{
                fontSize: 'clamp(22px, 3.2vw, 36px)',
                fontWeight: 800,
                letterSpacing: '-0.025em',
                lineHeight: 1.15,
                color: C.navy,
                fontFamily: SERIF,
                marginBottom: 16,
              }}>
                Deep. Curated.<br />Built for decision-makers.
              </div>

              <p style={{
                fontSize: 14.5, lineHeight: 1.65, color: C.mutedTxt,
                maxWidth: 390, marginBottom: 28, fontFamily: SANS,
              }}>
                Every Friday, The Week Ahead delivers 5–8 curated stories across
                markets, policy, and opportunity — in roughly 8 minutes —
                so you enter the weekend with{' '}
                <strong style={{ color: C.navy }}>full situational awareness</strong>{' '}
                and a clear plan.
              </p>

              {/* CTAs */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
                <Link
                  to="/subscribe"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: C.teal, color: '#fff',
                    padding: '13px 22px', borderRadius: 6,
                    fontFamily: SANS, fontWeight: 700, fontSize: 13.5,
                    letterSpacing: '0.04em', textDecoration: 'none',
                    textTransform: 'uppercase',
                  }}
                  data-testid="twa-cta-primary"
                >
                  GET THE WEEK AHEAD FREE
                </Link>
                <Link
                  to="/newsletter"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: '#fff', color: C.navy,
                    border: `1.5px solid ${C.border2}`,
                    padding: '13px 18px', borderRadius: 6,
                    fontFamily: SANS, fontWeight: 700, fontSize: 13.5,
                    letterSpacing: '0.04em', textDecoration: 'none',
                    textTransform: 'uppercase',
                  }}
                  data-testid="twa-cta-secondary"
                >
                  VIEW PAST EDITIONS
                </Link>
              </div>

              {/* Trust icons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <TrustRow label="Free for all subscribers" />
                <TrustRow label="No spam. One email. Every Friday." />
                <TrustRow label="Cancel anytime" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          STATS BAR — deep forest green
      ══════════════════════════════════════════════════════════════════ */}
      <div style={{ background: C.deepGreen }} data-testid="twa-stats-bar">
        <div className="container-custom" style={{ paddingTop: 28, paddingBottom: 28 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${STATS.length}, 1fr)`,
            gap: 0,
          }}>
            {STATS.map((s, i) => (
              <div
                key={s.val}
                style={{
                  textAlign: 'center',
                  padding: '4px 12px',
                  borderRight: i < STATS.length - 1
                    ? '1px solid rgba(255,255,255,.09)' : 'none',
                }}
                data-testid={`twa-stat-${i}`}
              >
                <div style={{
                  fontSize: 'clamp(18px, 2.5vw, 28px)',
                  fontWeight: 800, color: '#fff',
                  fontFamily: SANS, lineHeight: 1,
                }}>
                  {s.val}
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                  color: 'rgba(221,245,241,0.62)', marginTop: 5,
                  fontFamily: SANS, textTransform: 'uppercase',
                  lineHeight: 1.4, whiteSpace: 'pre-line',
                }}>
                  {s.lbl}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          RECENT EDITIONS + 4-WEEK ACCESS CARD
      ══════════════════════════════════════════════════════════════════ */}
      <section className="container-custom" style={{ paddingTop: 56, paddingBottom: 56 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 310px',
            gap: 28,
            alignItems: 'start',
          }}
          className="twa-week-grid"
        >

          {/* Edition list */}
          <div>
            <div style={{
              display: 'flex', alignItems: 'baseline',
              justifyContent: 'space-between', marginBottom: 18,
            }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: C.navy, fontFamily: SERIF }}>
                Recent Editions
              </h2>
              <Link to="/subscribe" style={{
                fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: C.teal,
                textDecoration: 'none', fontFamily: SANS,
              }}>
                View archive →
              </Link>
            </div>
            <p style={{ fontSize: 12.5, color: C.mutedTxt, marginBottom: 18, fontFamily: SANS }}>
              Members access the last 4 weeks of editions. Older editions move to the archive.
            </p>

            <div style={{
              border: `1px solid ${C.border2}`,
              borderRadius: 10, overflow: 'hidden',
            }}
              data-testid="twa-edition-list"
            >
              {EDITIONS.map((ed, i) => (
                <div
                  key={ed.vol}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 18px',
                    borderBottom: i < EDITIONS.length - 1
                      ? `1px solid ${C.border2}` : 'none',
                    background: i === 0 ? C.paper2 : '#fff',
                  }}
                  data-testid={`twa-edition-row-${i}`}
                >
                  <ReadIcon active={i === 0} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em',
                      color: C.muted, textTransform: 'uppercase',
                      fontFamily: SANS, marginBottom: 3,
                    }}>
                      {ed.date} · {ed.vol}
                    </div>
                    <div style={{
                      fontSize: 14, fontWeight: 700, color: C.navy,
                      fontFamily: SERIF, lineHeight: 1.3,
                      whiteSpace: 'nowrap', overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {i === 0 && (
                        <span style={{
                          display: 'inline-block', marginRight: 8,
                          background: C.teal, color: '#fff',
                          fontSize: 9, fontWeight: 800, letterSpacing: '0.08em',
                          textTransform: 'uppercase', padding: '2px 7px',
                          borderRadius: 20, verticalAlign: 'middle',
                          position: 'relative', top: -1, fontFamily: SANS,
                        }}>
                          THIS FRIDAY
                        </span>
                      )}
                      {ed.title}
                    </div>
                  </div>

                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'flex-end', gap: 3, flexShrink: 0,
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.navy, fontFamily: SANS }}>
                      {ed.readTime}
                    </span>
                    <span style={{ fontSize: 10.5, color: C.mutedTxt, fontFamily: SANS }}>
                      {ed.stories} STORIES
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 4-week access card */}
          <div style={{
            background: C.deepGreen, borderRadius: 12,
            padding: '28px 24px', color: '#fff',
          }}
            data-testid="twa-4-week-card"
          >
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'rgba(221,245,241,0.58)',
              fontFamily: SANS, marginBottom: 10,
            }}>
              4-WEEK ACCESS
            </div>
            <div style={{
              fontSize: 19, fontWeight: 800, color: '#fff',
              fontFamily: SERIF, marginBottom: 14, lineHeight: 1.2,
            }}>
              Members get rolling access to the last four editions.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
              {[
                { h: 'Friday delivery',   d: 'Your briefing arrives before the weekend starts.' },
                { h: 'Deep-dive format',  d: 'Not just headlines — context, implications, and action.' },
                { h: 'Older editions',    d: 'Editions older than 4 weeks move to the archive.' },
              ].map(item => (
                <div key={item.h}>
                  <div style={{
                    fontSize: 13, fontWeight: 700, color: '#fff',
                    fontFamily: SANS, marginBottom: 2,
                  }}>
                    {item.h}
                  </div>
                  <div style={{
                    fontSize: 12, color: 'rgba(221,245,241,0.62)',
                    fontFamily: SANS, lineHeight: 1.4,
                  }}>
                    {item.d}
                  </div>
                </div>
              ))}
            </div>
            <Link to="/subscribe" style={{
              display: 'block', textAlign: 'center',
              background: 'rgba(0,140,134,0.18)',
              border: `1px solid ${C.teal}`,
              color: C.teal, padding: '11px 0', borderRadius: 6,
              fontSize: 12, fontWeight: 800, letterSpacing: '0.08em',
              textTransform: 'uppercase', textDecoration: 'none',
              fontFamily: SANS,
            }}>
              EXPLORE THE ARCHIVE
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          WHAT'S INSIDE EVERY EDITION
      ══════════════════════════════════════════════════════════════════ */}
      <section style={{
        background: C.paper2,
        borderTop: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div className="container-custom" style={{ paddingTop: 52, paddingBottom: 52 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: C.teal, marginBottom: 12,
            fontFamily: SANS,
          }}>
            WHAT'S INSIDE EVERY EDITION
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 22,
            }}
            className="twa-coverage-grid"
            data-testid="twa-coverage"
          >
            {COVERAGE.map(item => (
              <div key={item.num} style={{
                background: C.paper,
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                padding: '22px 20px',
              }}>
                <div style={{
                  fontSize: 28, fontWeight: 900,
                  color: C.teal, fontFamily: SANS,
                  lineHeight: 1, marginBottom: 10,
                }}>
                  {item.num}
                </div>
                <div style={{
                  fontSize: 14, fontWeight: 800,
                  color: C.navy, fontFamily: SERIF, marginBottom: 7,
                }}>
                  {item.label}
                </div>
                <p style={{
                  fontSize: 12.5, color: C.mutedTxt,
                  lineHeight: 1.55, fontFamily: SANS,
                }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SUBSCRIBER REACTIONS
      ══════════════════════════════════════════════════════════════════ */}
      <section className="container-custom" style={{ paddingTop: 56, paddingBottom: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'baseline',
          justifyContent: 'space-between', marginBottom: 22,
        }}>
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: C.teal, marginBottom: 6,
              fontFamily: SANS,
            }}>
              SUBSCRIBER REACTIONS
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: C.navy, fontFamily: SERIF }}>
              What readers say about The Week Ahead.
            </h2>
          </div>
          <Link to="/subscribe" style={{
            fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: C.teal,
            textDecoration: 'none', fontFamily: SANS,
          }}>
            Join free →
          </Link>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 20,
            marginBottom: 48,
          }}
          className="twa-reactions-grid"
          data-testid="twa-testimonials"
        >
          {TESTIMONIALS.map(t => (
            <div key={t.name} style={{
              background: '#fff',
              border: `1px solid ${C.border2}`,
              borderRadius: 10,
              padding: '22px 22px 18px',
            }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{
                  fontSize: 14, fontWeight: 800,
                  color: C.navy, fontFamily: SANS,
                }}>
                  {t.name}
                </div>
                <div style={{
                  fontSize: 11.5, color: '#9a9189',
                  fontFamily: SANS, marginTop: 2,
                }}>
                  {t.role}
                </div>
              </div>
              <p style={{
                fontSize: 14.5, fontStyle: 'italic',
                color: '#4a5159', fontFamily: SERIF,
                lineHeight: 1.55, marginBottom: 14,
              }}>
                "{t.quote}"
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          CLAIM BAND — deep forest green
      ══════════════════════════════════════════════════════════════════ */}
      <section style={{ background: C.deepGreen }} data-testid="twa-claim-band">
        <div className="container-custom"
          style={{ paddingTop: 60, paddingBottom: 60, textAlign: 'center' }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: C.teal, marginBottom: 14,
            fontFamily: SANS,
          }}>
            THE WEEK AHEAD
          </div>
          <h2 style={{
            fontSize: 'clamp(22px, 3.8vw, 34px)',
            fontWeight: 800, color: '#fff',
            fontFamily: SERIF, lineHeight: 1.2, marginBottom: 10,
          }}>
            Enter every weekend prepared.
          </h2>
          <p style={{
            fontSize: 14, color: 'rgba(255,255,255,0.65)',
            fontFamily: SANS, maxWidth: 420,
            margin: '0 auto 28px', lineHeight: 1.6,
          }}>
            Free. No spam. One briefing that changes how you see the week ahead.
          </p>

          <Link
            to="/subscribe?product=the_week_ahead"
            data-testid="twa-signup-cta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: C.teal, color: '#fff', border: 'none',
              borderRadius: 6, padding: '14px 28px',
              fontFamily: SANS, fontWeight: 800, fontSize: 13,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              textDecoration: 'none', cursor: 'pointer',
            }}
          >
            GET IT FREE →
          </Link>

          <div style={{
            display: 'flex', gap: 20,
            justifyContent: 'center', marginTop: 18,
          }}>
            {['Free to subscribe', 'One email per week', 'Cancel anytime'].map(t => (
              <TrustRow key={t} label={t} light />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SPONSOR BAR
      ══════════════════════════════════════════════════════════════════ */}
      <div style={{
        borderTop: `1px solid ${C.border}`,
        padding: '18px 0',
        background: C.paper2,
      }}>
        <div className="container-custom" style={{
          display: 'flex', alignItems: 'center',
          gap: 10, justifyContent: 'center',
        }}>
          <span style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: '#b0a08a', fontFamily: SANS,
          }}>
            PRESENTED BY OPPORTUNITY POST — PROUDLY SPONSORED BY LEADERS WHO INVEST IN OPPORTUNITY.
          </span>
        </div>
        <div style={{
          display: 'flex', gap: 'clamp(16px, 3vw, 32px)',
          justifyContent: 'center', marginTop: 12, flexWrap: 'wrap',
        }}>
          {['JPMorgan Chase', 'Microsoft', 'Goldman Sachs', 'AWS', 'Deloitte', 'VISA'].map(s => (
            <span key={s} style={{
              fontSize: 12, fontWeight: 700, color: '#c0b49a',
              fontFamily: SANS, letterSpacing: '0.04em',
            }}>
              {s}
            </span>
          ))}
        </div>
      </div>

    </div>
  );
}
